import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";

const SUGGESTIONS = ["Should I sell now?", "What is a realistic exit timeline?", "How do I increase my EBITDA?"];

const RichText = ({ text }) => (
  <>
    {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={i} className="font-bold text-white">{part.slice(2, -2)}</strong>
      ) : (
        part
      )
    )}
  </>
);

export const ChatPanel = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [quota, setQuota] = useState(null);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);

  const load = () =>
    api.get("/chat/messages").then(({ data }) => {
      setMsgs(data.messages);
      setQuota(data);
    });

  useEffect(() => {
    if (open) load();
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, streaming]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || streaming) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: q }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Chat failed");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (p.startsWith("event: done")) continue;
          if (p.startsWith("event: error")) throw new Error("Model error");
          const line = p.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          let chunk;
          try {
            chunk = JSON.parse(line.slice(6));
          } catch {
            chunk = line.slice(6);
          }
          setMsgs((m) => {
            const c = [...m];
            c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + chunk };
            return c;
          });
        }
      }
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => m.slice(0, -2));
    } finally {
      setStreaming(false);
      load();
    }
  };

  if (!open)
    return (
      <button
        data-testid="chat-open-button"
        onClick={() => setOpen(true)}
        className="grad-btn fixed bottom-8 right-8 z-50 inline-flex items-center gap-3 px-6 py-4 rounded-full text-white font-bold"
      >
        <MessageSquare size={18} /> Ask the advisor
      </button>
    );

  return (
    <div
      data-testid="chat-panel"
      className="grad-ring fixed bottom-6 right-6 z-50 w-[min(430px,calc(100vw-3rem))] h-[min(640px,calc(100vh-4rem))] bg-[#12101E] rounded-[28px] shadow-[0_30px_90px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden"
    >
      <div className="flex items-start justify-between px-6 py-5 border-b border-[#262336]">
        <div>
          <p className="font-display text-xl font-bold grad-text">Exit Advisor</p>
          <p className="font-mono-fin text-[10px] uppercase tracking-[0.2em] text-[#9A93AD] mt-1" data-testid="chat-quota">
            {!quota
              ? "Loading quota"
              : quota.limit === null
              ? "Unlimited · Premium"
              : `${quota.remaining} of ${quota.limit} questions left`}
          </p>
        </div>
        <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="text-[#9A93AD] hover:text-[#FF6B35] transition-colors duration-200">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5" data-testid="chat-messages">
        {msgs.length === 0 && (
          <div className="space-y-3">
            <p className="text-sm text-[#9A93AD]">Ask anything about your valuation, buyers or exit timing.</p>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                data-testid={`chat-suggestion-${s.slice(0, 6).toLowerCase().replace(/\W/g, "")}`}
                onClick={() => send(s)}
                className="block w-full text-left px-4 py-3 rounded-[16px] border border-[#262336] bg-[#1C1930] text-sm font-semibold text-[#E9E4F5] hover:border-[#E1306C] hover:text-white transition-colors duration-200"
              >
                {s}
              </button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
            <div
              className={`max-w-[88%] px-4 py-3 rounded-[24px] text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "grad-bg text-white font-semibold" : "bg-[#1C1930] text-[#E9E4F5] border border-[#262336]"
              }`}
            >
              {m.content ? <RichText text={m.content} /> : streaming ? "…" : ""}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="border-t border-[#262336] p-4 flex items-center gap-3">
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask a question…"
          className="flex-1 bg-transparent outline-none text-sm px-2 py-2 text-white placeholder:text-[#6D6785]"
        />
        <button
          data-testid="chat-send-button"
          onClick={() => send()}
          disabled={streaming}
          className="grad-btn p-3 rounded-full text-white disabled:opacity-50"
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};
